import React, {useState} from "react"
import Box from "@mui/material/Box"
import classnames from 'classnames'
import {toast} from "react-toastify"
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom"

import './LoginPage.scss'
import {useAuthContext} from "../../contexts/AuthContext"
import {PasswordInput} from "../../components/PasswordInput"
import {useThemeStore} from "../../stores/themeStore"
import fetcher from "../../fetcher"
import TokenService from "../../utils/TokenService"

// The TrueDesking wordmark logo (inverted to white for the dark-gradient
// brand panel on the left).
const TRUEDESKING_LOGO = "https://res.cloudinary.com/hgjsxpz9b/image/upload/v1765310173/Asset_4_4x-8_teyqav.png"

const BRAND_PILLS = [
    'Lease & Finance Calculator',
    'Multi-Lender Grid',
    'Rebate Workbench',
    'CRM Pipeline',
]

export const Loginpage = () => {
    const { login } = useAuthContext()
    const theme = useThemeStore((state) => state.theme)
    const navigate = useNavigate()

    // The mode lives in the URL: React Router reuses this instance across the
    // /login and /reset-password routes, so local mode state could go stale.
    const { uid, token } = useParams()
    const [searchParams] = useSearchParams()
    const isReset = Boolean(token)
    const isForgot = !isReset && searchParams.has('forgot')

    const [emailOrUsername, setEmailOrUsername] = useState('')
    const [password,        setPassword]        = useState('')
    const [rememberDevice,  setRememberDevice]  = useState(true)

    const [isPending,       setIsPending]       = useState(false)
    const [resetSent,       setResetSent]       = useState(false)
    const [newPassword,     setNewPassword]     = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [resetErrors,     setResetErrors]     = useState(null)

    const isDisabled = !emailOrUsername || !password
    const isForgotDisabled = !emailOrUsername.trim() || isPending
    const isResetDisabled = !newPassword || newPassword !== confirmPassword || isPending

    // A 400 without field errors means the link itself was rejected.
    const fieldErrors = [...(resetErrors?.new_password1 ?? []), ...(resetErrors?.new_password2 ?? [])]
    const isLinkInvalid = Boolean(resetErrors) && fieldErrors.length === 0

    const onSubmit = (e) => {
        e.preventDefault()
        return login(emailOrUsername.toLowerCase(), password)
            .catch((err) => {
                toast.error(err?.response?.data?.detail || 'Login failed. Please try again.')
            })
    }

    // Errors (a 429 included) are already toasted by the axios interceptor.
    const onForgotSubmit = (e) => {
        e.preventDefault()
        setIsPending(true)
        fetcher.requestPasswordReset(emailOrUsername.trim())
            .then(() => setResetSent(true))
            .catch(() => {})
            .finally(() => setIsPending(false))
    }

    const onResetSubmit = (e) => {
        e.preventDefault()
        setIsPending(true)
        setResetErrors(null)
        fetcher.confirmPasswordReset({uid, token, new_password1: newPassword, new_password2: confirmPassword})
            .then(() => {
                // The reset signed out every session server-side; drop any stale tokens here too.
                TokenService.removeTokens()
                toast.success('Password updated. Please sign in with your new password.')
                navigate('/login', {replace: true})
            })
            .catch((err) => setResetErrors(err?.response?.status === 400 ? err.response.data : null))
            .finally(() => setIsPending(false))
    }

    const forgotLink = (text) => (
        <Link to={'/login?forgot'} className={'form-link'} onClick={() => setResetSent(false)}>{text}</Link>
    )
    const backLink = <Link to={'/login'} className={'form-link form-back'}>Back to sign in</Link>

    return (
        <Box id="LoginPage" data-theme={theme}>
            {/* ── Left: brand panel (hidden on tablet/mobile) ───────────── */}
            <div className={'login-brand-panel'}>
                <div className={'brand-header'}>
                    <img
                        className={'brand-logo'}
                        src={TRUEDESKING_LOGO}
                        alt={'TrueDesking'}
                    />
                </div>

                <div className={'brand-content'}>
                    <h1 className={'brand-heading'}>
                        Structure Deals.<br/>
                        Quote Faster.<br/>
                        Close More.
                    </h1>
                    <p className={'brand-description'}>
                        TrueDesking gives your team one place to build quotes,
                        compare lenders, apply rebates, and present numbers
                        your customers can say yes to.
                    </p>
                    <div className={'brand-pills'}>
                        {BRAND_PILLS.map((pill) => (
                            <span key={pill} className={'brand-pill'}>{pill}</span>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Right: login form ─────────────────────────────────────── */}
            <div className={'login-form-panel'}>
                <div className={'form-container'}>
                    <p className={'form-eyebrow'}>User Portal</p>
                    {isReset ? (
                        <>
                            <h2 className={'form-heading'}>Set a New Password</h2>
                            {isLinkInvalid ? (
                                <p className={'form-description'} role={'alert'}>
                                    This password reset link is invalid or has expired.{' '}
                                    {forgotLink('Request a new link')}
                                </p>
                            ) : (
                                <form onSubmit={onResetSubmit} noValidate>
                                    <div className={'form-field'}>
                                        <label htmlFor={'reset-password'}>New Password</label>
                                        <PasswordInput
                                            id={'reset-password'}
                                            autoComplete={'new-password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                        />
                                    </div>

                                    <div className={'form-field'}>
                                        <label htmlFor={'reset-confirm'}>Confirm New Password</label>
                                        <PasswordInput
                                            id={'reset-confirm'}
                                            autoComplete={'new-password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                        />
                                        {fieldErrors.map((message) => (
                                            <span className={'form-error'} key={message}>{message}</span>
                                        ))}
                                    </div>

                                    <button
                                        type={'submit'}
                                        disabled={isResetDisabled}
                                        className={classnames('signin-btn', {disabled: isResetDisabled})}
                                    >
                                        Update Password
                                    </button>
                                </form>
                            )}
                            {backLink}
                        </>
                    ) : isForgot ? (
                        <>
                            <h2 className={'form-heading'}>Reset Password</h2>
                            {resetSent ? (
                                <p className={'form-description'} role={'status'}>
                                    {"If an account exists for that email, we've sent password-reset instructions."}
                                </p>
                            ) : (
                                <form onSubmit={onForgotSubmit} noValidate>
                                    <div className={'form-field'}>
                                        <label htmlFor={'forgot-email'}>Email</label>
                                        <input
                                            id={'forgot-email'}
                                            className={'form-input'}
                                            type={'email'}
                                            autoComplete={'email'}
                                            placeholder={'you@dealer-group.com'}
                                            value={emailOrUsername}
                                            onChange={(e) => setEmailOrUsername(e.target.value)}
                                        />
                                    </div>

                                    <button
                                        type={'submit'}
                                        disabled={isForgotDisabled}
                                        className={classnames('signin-btn', {disabled: isForgotDisabled})}
                                    >
                                        Send Reset Link
                                    </button>
                                </form>
                            )}
                            {backLink}
                        </>
                    ) : (
                        <>
                        <h2 className={'form-heading'}>Welcome Back</h2>
                        {/*<p className={'form-description'}>*/}
                        {/*    Sign in to access your deal desk — quotes, lender*/}
                        {/*    programs, pricing, and your CRM pipeline are all*/}
                        {/*    waiting.*/}
                        {/*</p>*/}
    
                        <form onSubmit={onSubmit} noValidate autoComplete={'off'}>
                            <div className={'form-field'}>
                                <label htmlFor={'login-email'}>Email or Username</label>
                                <input
                                    id={'login-email'}
                                    className={'form-input'}
                                    type={'text'}
                                    autoComplete={'username'}
                                    placeholder={'you@dealer-group.com'}
                                    value={emailOrUsername}
                                    onChange={(e) => setEmailOrUsername(e.target.value)}
                                />
                            </div>
    
                            <div className={'form-field'}>
                                <div className={'field-header'}>
                                    <label htmlFor={'login-password'}>Password</label>
                                    {forgotLink('Forgot password?')}
                                </div>
                                <PasswordInput
                                    id={'login-password'}
                                    className={'form-input'}
                                    autoComplete={'current-password'}
                                    placeholder={'••••••••'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
    
                            <div className={'form-remember'}>
                                <input
                                    id={'remember'}
                                    type={'checkbox'}
                                    checked={rememberDevice}
                                    onChange={(e) => setRememberDevice(e.target.checked)}
                                />
                                <label htmlFor={'remember'}>Remember This Device</label>
                                <span className={'remember-aside'}>Keep me signed in</span>
                            </div>
    
                            <button
                                type={'submit'}
                                disabled={isDisabled}
                                className={classnames('signin-btn', {disabled: isDisabled})}
                            >
                                Sign In
                            </button>
                        </form>
    
                        </>
                    )}

                    <p className={'form-footer'}>
                        No account? Contact your admin.
                    </p>
                </div>
            </div>
        </Box>
    )
}
